import { useState } from "react";
import { SavedSearchItem } from "../types/common";
import { SavedFolder } from "../types/folder";

const STORAGE_KEY = "lusca-search-folders";

interface UseFoldersReturn {
  folders: SavedFolder[];
  saveFolder: (name: string) => void;
  updateFolder: (id: string, newName: string) => void;
  deleteFolder: (id: string) => void;
  addTransactions: (
    folderId: string,
    transactions: SavedSearchItem[]
  ) => {
    success: boolean;
    message: string;
    addedCount: number;
    duplicateCount: number;
  };
  deleteTransaction: (folderId: string, transactionId: string) => void;
  updateTransactionLabels: (
    folderId: string,
    transactionId: string,
    labels: string[]
  ) => void;
}

export default function useFolders(): UseFoldersReturn {
  const [folders, setFolders] = useState<SavedFolder[]>(() => {
    const savedFolders = localStorage.getItem(STORAGE_KEY);
    return savedFolders ? JSON.parse(savedFolders) : [];
  });

  const saveFoldersToStorage = (updatedFolders: SavedFolder[]) => {
    setFolders(updatedFolders);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(updatedFolders));
  };

  const isFolderNameExists = (name: string, excludeId?: string) =>
    folders.some(
      (folder) =>
        folder.name.toLowerCase() === name.toLowerCase() &&
        folder.id !== excludeId
    );

  const updateFolder = (folderId: string, newName: string): void => {
    if (isFolderNameExists(newName, folderId)) {
      throw new Error("A folder with this name already exists");
    }
    const updatedFolders = folders.map((folder) =>
      folder.id === folderId ? { ...folder, name: newName } : folder
    );
    saveFoldersToStorage(updatedFolders);
  };

  const saveFolder = (folderName: string): void => {
    if (isFolderNameExists(folderName)) {
      throw new Error("A folder with this name already exists");
    }
    const newFolder: SavedFolder = {
      id: crypto.randomUUID(),
      name: folderName,
      createdAt: new Date().toISOString(),
      transactions: [],
      filters: {
        stage: undefined,
        gl: undefined,
        marketplaces: undefined,
        timeRange: undefined,
      },
    };
    saveFoldersToStorage([...folders, newFolder]);
  };

  const deleteFolder = (folderId: string) => {
    const updatedFolders = folders.filter((folder) => folder.id !== folderId);
    saveFoldersToStorage(updatedFolders);
  };

  const addTransactions = (
    folderId: string,
    transactions: SavedSearchItem[]
  ) => {
    const existingIds = new Set(
      folders
        .find((folder) => folder.id === folderId)
        ?.transactions?.map((t) => t.id) || []
    );

    const newTransactions = transactions.filter((t) => !existingIds.has(t.id));
    const duplicateTransactions = transactions.filter((t) =>
      existingIds.has(t.id)
    );

    if (newTransactions.length === 0) {
      return {
        success: false,
        message: `All ${transactions.length} transaction(s) already exist in this folder`,
        addedCount: 0,
        duplicateCount: duplicateTransactions.length,
      };
    }

    const updatedFolders = folders.map((folder) => {
      if (folder.id !== folderId) return folder;

      return {
        ...folder,
        transactions: [...(folder.transactions || []), ...newTransactions],
      };
    });

    saveFoldersToStorage(updatedFolders);

    const addedCount = newTransactions.length;
    const duplicateCount = duplicateTransactions.length;

    const message =
      duplicateCount > 0
        ? `Successfully added ${addedCount} new transaction(s). ${duplicateCount} duplicate(s) were skipped`
        : `Successfully added ${addedCount} new transaction(s)`;

    return {
      success: true,
      message,
      addedCount,
      duplicateCount,
    };
  };

  const deleteTransaction = (folderId: string, transactionId: string) => {
    const updatedFolders = folders.map((folder) =>
      folder.id === folderId
        ? {
            ...folder,
            transactions:
              folder.transactions?.filter((t) => t.id !== transactionId) || [],
          }
        : folder
    );
    saveFoldersToStorage(updatedFolders);
  };

  const updateTransactionLabels = (
    folderId: string,
    transactionId: string,
    labels: string[]
  ) => {
    const updatedFolders = folders.map((folder) => {
      if (folder.id !== folderId) return folder;
      return {
        ...folder,
        transactions: folder.transactions?.map((transaction) =>
          transaction.id === transactionId
            ? { ...transaction, labels }
            : transaction
        ),
      };
    });
    saveFoldersToStorage(updatedFolders);
  };

  return {
    folders,
    saveFolder,
    updateFolder,
    deleteFolder,
    addTransactions,
    deleteTransaction,
    updateTransactionLabels,
  };
}
